import { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic, X, Volume2, MicOff } from 'lucide-react';

const VOICE_API_URL = 'https://gjyl-voice-ai.gjyl.workers.dev';

// VAD Settings
const SILENCE_THRESHOLD = 0.01; // Audio level below this = silence
const SILENCE_DURATION = 1500; // ms of silence before auto-send
const MIN_RECORDING_TIME = 500; // Minimum recording time before checking silence

// Feature detection for iOS compatibility
const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
const hasContinuousSpeech = (() => {
  const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!SpeechRecognition) return false;
  // iOS Safari doesn't support continuous mode properly
  return !isIOS;
})();
const hasMediaRecorder = typeof MediaRecorder !== 'undefined';

export default function GilAssistant({ onCommand, friends = [], userName }) {
  const [state, setState] = useState('idle'); // 'idle' | 'listening' | 'thinking' | 'speaking' | 'error'
  const [isOpen, setIsOpen] = useState(false);
  const [isHidden, setIsHidden] = useState(false); // Double-tap hides button
  const [transcript, setTranscript] = useState('');
  const [response, setResponse] = useState('');
  const [error, setError] = useState('');
  const [audioLevel, setAudioLevel] = useState(0);

  // Position state for dragging
  const [position, setPosition] = useState(() => {
    const saved = localStorage.getItem('gil-position');
    return saved ? JSON.parse(saved) : { x: 0, y: 0 };
  });

  // Audio refs
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);
  const streamRef = useRef(null);

  // VAD refs
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const silenceStartRef = useRef(null);
  const recordingStartRef = useRef(null);
  const vadIntervalRef = useRef(null);

  // Double-tap detection
  const lastTapRef = useRef(0);
  const DOUBLE_TAP_DELAY = 300;

  // Wake word detection refs
  const wakeWordRecognitionRef = useRef(null);

  // Save position to localStorage
  useEffect(() => {
    localStorage.setItem('gil-position', JSON.stringify(position));
  }, [position]);

  // Initialize wake word detection ("Hey Gil" / "Hi Gil") - Desktop only
  useEffect(() => {
    // iOS doesn't support continuous speech recognition - skip wake word detection
    if (!hasContinuousSpeech) {
      console.log('[Gil] Wake word detection not available (iOS or unsupported browser)');
      return;
    }

    if (isHidden) {
      startWakeWordDetection();
    } else {
      stopWakeWordDetection();
    }
    return () => stopWakeWordDetection();
  }, [isHidden]);

  const startWakeWordDetection = () => {
    // Skip on iOS - continuous recognition not supported
    if (!hasContinuousSpeech) {
      console.log('[Gil] Wake word detection skipped - not supported on this device');
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = navigator.language || 'en-US';

    recognition.onresult = (event) => {
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript.toLowerCase();
        // Check for wake words
        if (transcript.includes('hey gil') ||
            transcript.includes('hi gil') ||
            transcript.includes('okay gil') ||
            transcript.includes('show gil') ||
            transcript.includes('gil button')) {
          setIsHidden(false);
          stopWakeWordDetection();
          break;
        }
      }
    };

    recognition.onerror = (event) => {
      if (event.error !== 'no-speech') {
        console.log('[Gil] Wake word detection error:', event.error);
      }
    };

    recognition.onend = () => {
      // Restart if still hidden
      if (isHidden && wakeWordRecognitionRef.current) {
        try {
          recognition.start();
        } catch (e) {
          // Already started
        }
      }
    };

    wakeWordRecognitionRef.current = recognition;
    try {
      recognition.start();
      console.log('[Gil] Wake word detection started - say "Hey Gil" to show');
    } catch (e) {
      console.error('[Gil] Failed to start wake word detection:', e);
    }
  };

  const stopWakeWordDetection = () => {
    if (wakeWordRecognitionRef.current) {
      wakeWordRecognitionRef.current.stop();
      wakeWordRecognitionRef.current = null;
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
      if (vadIntervalRef.current) {
        clearInterval(vadIntervalRef.current);
      }
      stopWakeWordDetection();
    };
  }, []);

  // Voice Activity Detection - analyze audio levels
  const startVAD = useCallback(async (stream) => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();

      // iOS requires AudioContext to be resumed after user gesture
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
        console.log('[Gil] AudioContext resumed for iOS');
      }

      const analyser = audioContext.createAnalyser();
      const source = audioContext.createMediaStreamSource(stream);

      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.8;
      source.connect(analyser);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;
      silenceStartRef.current = null;
      recordingStartRef.current = Date.now();

      const dataArray = new Uint8Array(analyser.frequencyBinCount);

      vadIntervalRef.current = setInterval(() => {
        if (state !== 'listening') return;

        analyser.getByteFrequencyData(dataArray);

        // Calculate average audio level
        const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length / 255;
        setAudioLevel(average);

        const timeSinceStart = Date.now() - recordingStartRef.current;

        // Only check for silence after minimum recording time
        if (timeSinceStart < MIN_RECORDING_TIME) return;

        if (average < SILENCE_THRESHOLD) {
          // Silence detected
          if (!silenceStartRef.current) {
            silenceStartRef.current = Date.now();
          } else if (Date.now() - silenceStartRef.current > SILENCE_DURATION) {
            // Silence for long enough - auto-send
            console.log('[Gil] Silence detected - auto-sending');
            stopRecording();
          }
        } else {
          // Sound detected - reset silence timer
          silenceStartRef.current = null;
        }
      }, 100);
    } catch (err) {
      console.error('[Gil] VAD setup failed:', err);
    }
  }, [state]);

  const stopVAD = useCallback(() => {
    if (vadIntervalRef.current) {
      clearInterval(vadIntervalRef.current);
      vadIntervalRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    silenceStartRef.current = null;
    setAudioLevel(0);
  }, []);

  // Start recording with VAD
  const startRecording = useCallback(async () => {
    try {
      setError('');

      // Check if MediaRecorder is available (not on older iOS)
      if (!hasMediaRecorder) {
        setError('Voice recording not supported on this device. Try using text input instead.');
        setState('error');
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Determine best mimeType - try webm first (Chrome/Firefox), then mp4 (Safari), then wav
      let mimeType = 'audio/webm';
      if (!MediaRecorder.isTypeSupported('audio/webm')) {
        if (MediaRecorder.isTypeSupported('audio/mp4')) {
          mimeType = 'audio/mp4';
        } else if (MediaRecorder.isTypeSupported('audio/wav')) {
          mimeType = 'audio/wav';
        } else {
          // Use default
          mimeType = '';
        }
      }

      const mediaRecorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);

      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        stopVAD();
        const audioBlob = new Blob(audioChunksRef.current, { type: mediaRecorder.mimeType || 'audio/webm' });
        await processAudio(audioBlob);
      };

      mediaRecorder.start();
      setState('listening');

      // Start VAD
      await startVAD(stream);
    } catch (err) {
      console.error('Failed to start recording:', err);
      if (err.name === 'NotAllowedError') {
        setError('Microphone access denied. Please allow microphone access.');
      } else if (err.name === 'NotFoundError') {
        setError('No microphone found on this device.');
      } else {
        setError('Could not access microphone. Try using text input.');
      }
      setState('error');
    }
  }, [startVAD, stopVAD]);

  // Stop recording
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      setState('thinking');
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    stopVAD();
  }, [stopVAD]);

  // Get user's browser language
  const getUserLanguage = () => {
    const lang = navigator.language || navigator.userLanguage || 'en';
    return lang.split('-')[0];
  };

  // Process audio through the voice pipeline
  const processAudio = async (audioBlob) => {
    try {
      setState('thinking');

      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      formData.append('context', JSON.stringify({
        friends: friends.map(f => ({
          ...f,
          language: f.language || f.preferred_language
        })),
        user_name: userName,
        user_language: getUserLanguage(),
      }));

      const res = await fetch(`${VOICE_API_URL}/voice`, {
        method: 'POST',
        body: formData,
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Failed to process audio');
      }

      const transcriptHeader = res.headers.get('X-Transcript');
      const responseHeader = res.headers.get('X-Response-Text');

      if (transcriptHeader) {
        setTranscript(decodeURIComponent(transcriptHeader));
      }
      if (responseHeader) {
        setResponse(decodeURIComponent(responseHeader));
      }

      const audioData = await res.arrayBuffer();
      await playAudio(audioData);

      if (onCommand && transcriptHeader && responseHeader) {
        onCommand(
          decodeURIComponent(transcriptHeader),
          decodeURIComponent(responseHeader)
        );
      }
    } catch (err) {
      console.error('Error processing audio:', err);
      setError(err instanceof Error ? err.message : 'Failed to process audio');
      setState('error');
    }
  };

  // Play audio response
  const playAudio = async (audioData) => {
    try {
      setState('speaking');

      const audioBlob = new Blob([audioData], { type: 'audio/wav' });
      const audioUrl = URL.createObjectURL(audioBlob);

      if (audioRef.current) {
        audioRef.current.pause();
        URL.revokeObjectURL(audioRef.current.src);
      }

      const audio = new Audio(audioUrl);
      audioRef.current = audio;

      audio.onended = () => {
        setState('idle');
        URL.revokeObjectURL(audioUrl);
      };

      audio.onerror = () => {
        setError('Failed to play audio');
        setState('error');
        URL.revokeObjectURL(audioUrl);
      };

      await audio.play();
    } catch (err) {
      console.error('Error playing audio:', err);
      setError('Failed to play response');
      setState('error');
    }
  };

  // Handle button tap with double-tap detection
  const handleButtonTap = () => {
    const now = Date.now();
    const timeSinceLastTap = now - lastTapRef.current;

    if (timeSinceLastTap < DOUBLE_TAP_DELAY) {
      // Double tap - hide button
      setIsHidden(true);
      console.log('[Gil] Button hidden - say "Hey Gil" to show');
    } else {
      // Single tap - open assistant
      handleActivate();
    }

    lastTapRef.current = now;
  };

  const handleActivate = () => {
    setIsOpen(true);
    setTranscript('');
    setResponse('');
    setError('');
    startRecording();
  };

  const handleClose = () => {
    setIsOpen(false);
    setState('idle');
    setTranscript('');
    setResponse('');
    setError('');
    stopRecording();
    if (audioRef.current) {
      audioRef.current.pause();
    }
  };

  const handleMicToggle = () => {
    if (state === 'listening') {
      stopRecording();
    } else if (state === 'idle' || state === 'error') {
      setError('');
      startRecording();
    }
  };

  // Text message fallback
  const sendTextMessage = async (text) => {
    try {
      setState('thinking');
      setTranscript(text);
      setResponse('');
      setError('');

      const res = await fetch(`${VOICE_API_URL}/llm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text,
          context: {
            friends: friends.map(f => ({
              ...f,
              language: f.language || f.preferred_language
            })),
            user_name: userName,
            user_language: getUserLanguage(),
          },
        }),
      });

      if (!res.ok) {
        throw new Error(`LLM failed: ${res.status}`);
      }

      const data = await res.json();
      setResponse(data.response);

      const ttsRes = await fetch(`${VOICE_API_URL}/tts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: data.response }),
      });

      if (ttsRes.ok) {
        const audioData = await ttsRes.arrayBuffer();
        await playAudio(audioData);
      } else {
        setState('idle');
      }

      if (onCommand) {
        onCommand(text, data.response);
      }
    } catch (err) {
      console.error('[Gil] Error:', err);
      setError(err.message || 'Failed to send message');
      setState('error');
    }
  };

  const stateColors = {
    idle: 'from-teal-500 to-blue-500',
    listening: 'from-teal-500 to-teal-400',
    thinking: 'from-blue-500 to-purple-500',
    speaking: 'from-teal-500 to-blue-500',
    error: 'from-red-500 to-red-600',
  };

  const stateText = {
    idle: 'Tap to speak',
    listening: 'Listening... (auto-sends when you stop)',
    thinking: 'Thinking...',
    speaking: 'Speaking...',
    error: error || 'Something went wrong',
  };

  // Don't render button if hidden
  if (isHidden) {
    return null; // Wake word detection still runs in background
  }

  return (
    <>
      {/* Floating Activation Button - Draggable */}
      <motion.button
        onClick={handleButtonTap}
        drag
        dragMomentum={false}
        dragElastic={0.1}
        onDragEnd={(event, info) => {
          setPosition({ x: info.offset.x, y: info.offset.y });
        }}
        style={{ x: position.x, y: position.y, touchAction: 'manipulation' }}
        className="fixed bottom-6 right-6 z-50 group cursor-grab active:cursor-grabbing touch-manipulation"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        {/* Outer glow ring */}
        <div className="absolute inset-0 rounded-full bg-gradient-to-r from-teal-500 to-blue-500 opacity-20 blur-xl group-hover:opacity-40 transition-opacity" />

        {/* Pulsing ring animation */}
        <motion.div
          className="absolute inset-0 rounded-full border-2 border-teal-500/30"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.5, 0, 0.5],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />

        {/* Main button */}
        <div className="relative w-14 h-14 rounded-full bg-gradient-to-br from-teal-500 to-blue-500 p-[2px] shadow-lg shadow-teal-500/25">
          <div className="w-full h-full rounded-full bg-slate-900/90 backdrop-blur-xl flex items-center justify-center overflow-hidden">
            <img
              src="/logos/gjyl-logo-oauth-512.png"
              alt="Gil"
              className="w-10 h-10 rounded-full object-cover"
            />
          </div>
        </div>

        {/* Tooltip */}
        <div className="absolute bottom-full right-0 mb-2 px-3 py-1.5 rounded-lg bg-slate-800/95 backdrop-blur-sm border border-white/10 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
          <span className="text-sm text-slate-200">Hey Gil</span>
          <span className="text-xs text-slate-400 ml-2">
            {hasContinuousSpeech ? 'Double-tap to hide' : 'Tap to speak'}
          </span>
        </div>
      </motion.button>

      {/* Full Assistant Modal */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center"
          >
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={handleClose}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />

            {/* Assistant Card */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="relative w-full max-w-md mx-4"
            >
              <div className="relative rounded-3xl bg-gradient-to-b from-slate-800/95 to-slate-900/98 backdrop-blur-xl border border-white/10 shadow-2xl shadow-black/50 overflow-hidden">

                {/* Top glow */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3/4 h-32 bg-gradient-to-b from-teal-500/20 to-transparent blur-2xl pointer-events-none" />

                {/* Close button */}
                <button
                  onClick={handleClose}
                  className="absolute top-4 right-4 p-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors z-10"
                >
                  <X className="w-5 h-5 text-slate-400" />
                </button>

                {/* Content */}
                <div className="relative p-8 pt-12 flex flex-col items-center">

                  {/* Animated Orb */}
                  <button
                    onClick={handleMicToggle}
                    disabled={state === 'thinking' || state === 'speaking'}
                    className="relative w-32 h-32 mb-6 disabled:cursor-not-allowed"
                  >
                    {/* Outer rings */}
                    <motion.div
                      className={`absolute inset-0 rounded-full bg-gradient-to-r ${stateColors[state]} opacity-10`}
                      animate={state === 'listening' ? {
                        scale: [1, 1 + audioLevel * 0.5, 1],
                        opacity: [0.1, 0.05 + audioLevel * 0.1, 0.1],
                      } : {}}
                      transition={{ duration: 0.1 }}
                    />
                    <motion.div
                      className={`absolute inset-2 rounded-full bg-gradient-to-r ${stateColors[state]} opacity-20`}
                      animate={state === 'listening' ? {
                        scale: [1, 1 + audioLevel * 0.3, 1],
                        opacity: [0.2, 0.1 + audioLevel * 0.15, 0.2],
                      } : {}}
                      transition={{ duration: 0.1 }}
                    />

                    {/* Main orb */}
                    <motion.div
                      className={`absolute inset-4 rounded-full bg-gradient-to-br ${stateColors[state]} p-[2px] shadow-lg`}
                      animate={state === 'listening' ? {
                        scale: [1, 1 + audioLevel * 0.1, 1],
                      } : state === 'thinking' ? {
                        rotate: 360,
                      } : {}}
                      transition={state === 'thinking' ? {
                        duration: 2,
                        repeat: Infinity,
                        ease: "linear",
                      } : {
                        duration: 0.1,
                      }}
                    >
                      <div className="w-full h-full rounded-full bg-slate-900/95 backdrop-blur-xl flex items-center justify-center">
                        {state === 'speaking' ? (
                          <Volume2 className="w-10 h-10 text-teal-500" />
                        ) : state === 'listening' ? (
                          <Mic className="w-10 h-10 text-teal-500" />
                        ) : state === 'error' ? (
                          <MicOff className="w-10 h-10 text-red-500" />
                        ) : (
                          <img
                            src="/logos/gjyl-logo-oauth-512.png"
                            alt="Gil"
                            className="w-14 h-14 rounded-full object-cover"
                          />
                        )}
                      </div>
                    </motion.div>

                    {/* Audio level indicator (when listening) */}
                    {state === 'listening' && (
                      <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                        {[...Array(5)].map((_, i) => (
                          <motion.div
                            key={i}
                            className="w-1 rounded-full bg-gradient-to-t from-teal-500 to-blue-500"
                            style={{
                              height: Math.max(8, audioLevel * 40 * (1 + Math.sin(Date.now() / 200 + i) * 0.3)),
                            }}
                          />
                        ))}
                      </div>
                    )}
                  </button>

                  {/* Status text */}
                  <motion.p
                    key={state}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`text-lg font-medium mb-2 ${state === 'error' ? 'text-red-500' : 'text-slate-200'}`}
                  >
                    {stateText[state]}
                  </motion.p>

                  {/* Transcript */}
                  {transcript && (
                    <motion.p
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="text-sm text-slate-400 text-center mb-4 max-w-xs"
                    >
                      "{transcript}"
                    </motion.p>
                  )}

                  {/* Response */}
                  {response && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="w-full p-4 rounded-xl bg-white/5 border border-white/10"
                    >
                      <p className="text-sm text-slate-200">{response}</p>
                    </motion.div>
                  )}

                  {/* Quick actions */}
                  <div className="mt-6 flex flex-wrap gap-2 justify-center">
                    {["Who's online?", 'Message...', 'Call...'].map((hint) => (
                      <button
                        key={hint}
                        onClick={() => sendTextMessage(hint.replace('...', ''))}
                        className="px-3 py-1 rounded-full text-xs text-slate-400 bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
                      >
                        {hint}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Bottom gradient */}
                <div className="h-1 bg-gradient-to-r from-teal-500 to-blue-500" />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
